import { GeneratedCollection } from "../generated.collection.abstraction";
import {
  type SymbolView,
  fromReferenceData,
  isManual,
  getSouceFromReferenceData,
} from "@bitwarden/web-vault/app/models/view/symbol/symbol.view";
import { computed, signal, Signal, WritableSignal } from "@angular/core";
import { Observable } from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";
import { ReferenceDataView } from "../../../models/view/reference-data/reference-data.view";
import { TransactionView } from "../../../models/view/transaction/transaction.view";
import { isCurrency } from "@bitwarden/web-vault/app/models/view/symbol/currency-utility";
import { isAfter, isBefore, isSameDay, max, min, startOfDay } from "date-fns";

export class SymbolsGeneratedCollection extends GeneratedCollection<SymbolView> {
  private _activeSymbols = new Set<string>();
  // KNOWN LIMITATION: this cache is derived from whatever referenceDataList was last
  // passed to generate(), not read live from the persisted reference-data store. It's
  // only as fresh as the most recent generate() call, and generate() only runs when
  // isDirtySymbols is set. transaction.store.service.ts's runForexStage consults this
  // cache before that same refresh cycle's runSymbolsStage/generate() call, so if a
  // referenceData change and a forex-triggering change (core currency / first
  // transaction date) get merged into the same cycle, the forex-fetch range decision
  // can be made against last cycle's coverage rather than this cycle's referenceData.
  private _symbolFetchDateRange = new Map<string, Date[]>();
  protected _collection: WritableSignal<SymbolView[]> = signal<SymbolView[]>(null);

  clear(): void {
    this._collection.set(null);
    this._activeSymbols.clear();
    this.clearFetchDateRange();
  }

  collection: Signal<SymbolView[]> = this._collection.asReadonly();
  collection$: Observable<SymbolView[]> = toObservable(this.collection);

  get activeSymbols(): string[] {
    return [...this._activeSymbols.values()];
  }

  /** Discards recorded fetch-date coverage - e.g. when the core currency changes and
   * previously recorded coverage no longer applies. recalcDateRange then falls back to
   * a full refetch on the next call. */
  clearFetchDateRange(): void {
    this._symbolFetchDateRange.clear();
  }

  /** Narrows [startDate, endDate] down to what each currency still needs fetched,
   * scoped per currency so one under-covered currency doesn't force a refetch of
   * currencies that are already fully covered. Returns null when every currency is
   * already covered - nothing to fetch. */
  recalcDateRange(
    currencies: string[],
    [startDate, endDate]: [Date, Date],
  ): { currencies: string[]; startDate: Date; endDate: Date } | null {
    const needed = currencies
      .map((currency) => ({
        currency,
        range: this.getCurrencyDateRange(currency, startDate, endDate),
      }))
      .filter(
        (entry): entry is { currency: string; range: { startDate: Date; endDate: Date } } =>
          entry.range !== null && !isAfter(entry.range.startDate, entry.range.endDate),
      );

    if (needed.length === 0) {
      return null;
    }

    return {
      currencies: needed.map(({ currency }) => currency),
      startDate: min(needed.map(({ range }) => range.startDate)),
      endDate: max(needed.map(({ range }) => range.endDate)),
    };
  }

  private getCurrencyDateRange(
    currency: string,
    startDate: Date,
    endDate: Date,
  ): { startDate: Date; endDate: Date } | null {
    const range = this._symbolFetchDateRange.get(currency);

    // fewer than 2 recorded dates means this currency's fetch history is
    // incomplete/unreliable — fall back to refetching its full requested range
    if (!range || range.length < 2) {
      return { startDate, endDate };
    }

    const [knownStart, knownEnd] = range;
    const coversStart = isSameDay(knownStart, startDate) || isBefore(knownStart, startDate);
    const coversEnd = isSameDay(knownEnd, endDate) || isAfter(knownEnd, endDate);

    // requested range is fully within known coverage - nothing to fetch
    if (coversStart && coversEnd) {
      return null;
    }

    // requested range surrounds known coverage on both sides - refetch everything
    if (!coversStart && !coversEnd) {
      return { startDate, endDate };
    }

    // caught up to startDate but not endDate - fetch forward from the last known day.
    // Deliberately re-fetches knownEnd itself (not knownEnd + 1) since the rate saved
    // for that day may not yet be the day's closing rate.
    if (coversStart) {
      return { startDate: knownEnd, endDate };
    }

    // caught up to endDate but not startDate - backfill the gap before known coverage,
    // for the same reason re-including knownStart itself.
    return { startDate, endDate: knownStart };
  }

  // currency symbols
  currencySymbols: Signal<SymbolView[]> = computed(() => {
    if (!this.collection()) {
      return null;
    }
    return this.collection().filter((s) => s.isCurrency());
  });
  currencySymbols$: Observable<SymbolView[]> = toObservable(this.currencySymbols);

  update(collection: SymbolView[]): void {}

  generate(
    baseCurrency: string,
    referenceDataList: ReferenceDataView[],
    transacions: TransactionView[],
  ): boolean {
    const prev = this.activeSymbols;
    this._activeSymbols.clear();
    this.clearFetchDateRange();
    this.updateActiveSymbolByTransactions(baseCurrency, transacions);
    const symbolsMap = this.gererateSymbolFromReferenceData(baseCurrency, referenceDataList);

    const isChanged =
      this._activeSymbols.size !== prev.length ||
      prev.findIndex((c) => !this._activeSymbols.has(c)) !== -1;

    // when returns true, need to pull new forex rates
    // If returns false, it means all the required rates are already in the symbols collection
    // check the refresh logic in transaction.store.service.ts
    if (!isChanged) {
      this.updateCollectionByMap(symbolsMap);
    }

    return isChanged;
  }

  private updateActiveSymbolByTransactions(baseCurrency: string, transactions: TransactionView[]) {
    for (const transaction of transactions) {
      const code = transaction?.quantity?.actualQuantity?.symbol;
      if (code && code !== baseCurrency) {
        this._activeSymbols.add(code);
        this.recordSymbolFetchDate(code);
      }
    }
  }

  private gererateSymbolFromReferenceData(
    baseCurrency: string,
    referenceDataList: ReferenceDataView[],
  ): Map<string, SymbolView> {
    const symbolsMap = new Map<string, SymbolView>();
    // todo @jun need to update the code with the new ReferenceDataView methods
    for (const referenceData of referenceDataList) {
      const { symbol, base, date } = referenceData;
      const isAutoImported = !isManual(getSouceFromReferenceData(referenceData));

      // skip if the auto-imported reference data is not the system preference currency
      // Only care about auto-imported symbols if they are the system preference currency,
      // according to how we fetch the forex data.
      // This is to avoid creating SymbolView that is not relevant to the user.
      // TODO: if it is manually added symbol, e.g. { base: BHP, symbol: AUD } (BHP in AUD) etc
      // we should generate a SymbolView for its symbol too.
      if (isAutoImported && symbol !== baseCurrency) {
        continue;
      } else if (symbol !== baseCurrency && !this._activeSymbols.has(symbol)) {
        this._activeSymbols.add(symbol);
        this.recordSymbolFetchDate(symbol);
      }

      const code = base;
      const reference = referenceData.clone();
      const symbolView =
        symbolsMap.get(code)?.addReference(reference) ?? fromReferenceData(reference);
      symbolsMap.set(code, symbolView);
      this._activeSymbols.add(code);

      // only counts as real fetch coverage when the rate is actually priced in the
      // current core currency - a manual cross rate (e.g. EUR priced in GBP while the
      // core currency is USD) says nothing about EUR/USD coverage
      // KNOWN LIMITATION: this doesn't check isAutoImported, so a manually-entered rate
      // priced in the core currency also counts as coverage. Two sparse manual entries
      // months apart can make recalcDateRange believe the whole gap between them is
      // already fetched, suppressing a real forex refetch for that range.
      if (symbol === baseCurrency) {
        this.recordSymbolFetchDate(code, new Date(date.date));
      }
    }
    return symbolsMap;
  }

  private updateCollectionByMap(map: Map<string, SymbolView>) {
    const res: SymbolView[] = [];
    map.forEach((symbolView, key, thisMap) => {
      if (!symbolView.isCurrency() && thisMap.has(symbolView.c_asOf.symbol)) {
        symbolView.link(thisMap.get(symbolView.c_asOf.symbol));
      }
      res.push(symbolView);
    });
    this._collection.set(res);
  }

  private recordSymbolFetchDate(code: string, date?: Date) {
    if (!isCurrency(code)) {
      return;
    }

    // Reject an unparsable reference-data date outright rather than let it poison
    // the recorded range - an Invalid Date would otherwise make every isBefore/isAfter/
    // isSameDay comparison against it evaluate false, corrupting coverage decisions.
    if (date && Number.isNaN(date.getTime())) {
      return;
    }

    // Normalize to day granularity up front so every comparison below (dedup and
    // min/max) treats two timestamps on the same calendar day as identical, instead
    // of as distinct instants that could widen the range or fake a second data point.
    const fetchDate = date ? startOfDay(date) : undefined;
    const range = this._symbolFetchDateRange.get(code);

    if (!range) {
      this._symbolFetchDateRange.set(code, fetchDate ? [fetchDate] : []);
      return;
    }

    if (!fetchDate) {
      return;
    }

    if (range.length === 0) {
      range.push(fetchDate);
      return;
    }

    // A lone recorded date stays lone until a genuinely different date shows up -
    // otherwise it would look like a complete [start, end] range of one identical day.
    if (isSameDay(range[0], fetchDate)) {
      return;
    }

    const [start, end] = range.length === 1 ? [range[0], range[0]] : range;
    this._symbolFetchDateRange.set(code, [min([fetchDate, start]), max([fetchDate, end])]);
  }
}
